import { type ActivityOf, getItem } from '@wyn/content';
import { createRng } from '../rng.ts';
import { BTN, DT, type RealtimeSim, TICK_HZ } from './realtime.ts';
import { clamp, type Fx, type MinigameResult } from './types.ts';

/** Working and quench bands (°C/14, i.e. percent of the gauge) and how fast each metal moves. */
export const METALS = {
  bronze: { name: 'Bronze', tier: 1, zone: [48, 66], quench: [34, 49], speed: 0.9 },
  iron: { name: 'Iron', tier: 2, zone: [58, 74], quench: [38, 52], speed: 1.15 },
  steel: { name: 'Steel', tier: 3, zone: [68, 82], quench: [43, 55], speed: 1.4 },
  damascus: { name: 'Damascus steel', tier: 4, zone: [77, 89], quench: [47, 57], speed: 1.7 },
  osmund: { name: 'Osmund iron', tier: 5, zone: [82, 91], quench: [49, 58], speed: 1.95 },
  starIron: { name: 'Star-iron', tier: 6, zone: [85, 93], quench: [51, 59], speed: 2.2 },
} as const;

const PAUSE_TICKS = Math.round(0.65 * TICK_HZ);
/** Each phase gives up after this long, so an idle forge still ends. */
const HEAT_LIMIT = 30;
const SHAPE_LIMIT = 40;

export type SmithingPhase = 'heat' | 'pause-shape' | 'shape' | 'pause-quench' | 'quench';

export interface SmithingSim extends RealtimeSim {
  phase: SmithingPhase;
  /** Gauge percent, 0–100 (the display shows ×14 as °C). */
  temp: number;
  vel: number;
  zone: readonly [number, number];
  quenchBand: readonly [number, number];
  /** The heat at which the coals scorch. */
  burnAt: number;
  /** Workability, 0–1. */
  heatProgress: number;
  /** The hammer mark, 0–1, and the target it swings across. */
  pos: number;
  center: number;
  /** Perfect and good widths, as fractions of the track. */
  perfect: number;
  good: number;
  strikes: number[];
  strikesNeeded: number;
  /** Seconds until the hammer can strike again. */
  cool: number;
}

/**
 * The Great Forge: hold to pump the bellows and keep the metal in its working band, strike as the
 * hammer crosses the mark, then quench as the needle cools through the blue band. Ported from the
 * Harrowmere prototype.
 */
export function smithingSim(a: ActivityOf<'smithing'>, level: number, seed: string): SmithingSim {
  const rng = createRng(seed);
  const m = METALS[a.metal];
  const [lo, hi] = m.zone;
  const mid = (lo + hi) / 2, half = (hi - lo) / 2;
  const [q1, q2] = m.quench;
  const qm = (q1 + q2) / 2, qh = (q2 - q1) / 2;
  const rate = 16 * m.speed;
  const product = getItem(a.product).name;

  let prev = 0;
  let phaseT = 0, pause = 0;
  // Heat.
  let qs = 0, qn = 0, scorch = 0, warned = -1, heat = 0;
  // Shape.
  let dir = 1;
  let hammer = 0;
  const speed = 0.8 * m.speed;

  const fx = (text: string, tone: Fx['tone'], cue?: string, power?: number) => sim.fx.push({ tick: sim.tick, text, tone, cue, power });
  const end = (quench: number, trueTemper = false) => {
    const score = heat * 0.3 + hammer * 0.5 + quench * 0.2;
    const allPerfect = sim.strikes.length === a.strikes && sim.strikes.every((q) => q === 1);
    const r: MinigameResult = {
      score,
      title: `You forge ${/^[aeiou]/i.test(product) ? 'an' : 'a'} ${product.toLowerCase()}`,
      text: score > 0.75 ? 'The smith nods at your work.' : score > 0.45 ? 'Serviceable, if not handsome.' : 'It will hold together. Probably.',
      breakdown: [['Heating', heat], ['Shaping', hammer], ['Quenching', quench]],
      yield: { itemKey: a.product, quantity: 1, graded: true },
      coin: 0,
      xpMult: 1,
      feats: [...(trueTemper ? ['true-temper'] : []), ...(allPerfect ? ['all-perfect'] : [])],
    };
    sim.result = r;
  };
  const toShape = () => {
    sim.phase = 'pause-shape';
    pause = PAUSE_TICKS;
  };
  const finishShape = () => {
    hammer = sim.strikes.length ? sim.strikes.reduce((x, y) => x + y, 0) / sim.strikesNeeded : 0;
    sim.phase = 'pause-quench';
    pause = PAUSE_TICKS;
  };

  const perfect = Math.max(0.05, 0.11 - m.tier * 0.013) + clamp((level - a.level) * 0.004, 0, 0.04);
  const sim: SmithingSim = {
    tick: 0,
    result: null,
    fx: [],
    phase: 'heat',
    temp: 22,
    vel: 0,
    zone: m.zone,
    quenchBand: m.quench,
    burnAt: Math.min(97, hi + 8),
    heatProgress: 0,
    pos: 0,
    center: 0.5,
    perfect,
    good: perfect * 2.8,
    strikes: [],
    strikesNeeded: a.strikes,
    cool: 0,
    step(mask) {
      if (sim.result) return;
      const held = (mask & BTN.main) !== 0;
      const pressed = held && !(prev & BTN.main);
      prev = mask;
      sim.tick++;

      switch (sim.phase) {
        case 'heat': {
          phaseT += DT;
          sim.vel += ((held ? 46 : -20) - sim.vel) * Math.min(1, DT * 3.2);
          sim.temp = clamp(sim.temp + sim.vel * DT, 15, 100);
          if (sim.temp >= lo && sim.temp <= hi) {
            sim.heatProgress += DT / 2.8;
            qs += (1 - (Math.abs(sim.temp - mid) / half) * 0.5) * DT;
            qn += DT;
          }
          if (sim.temp >= sim.burnAt) {
            scorch += DT;
            if (phaseT - warned > 0.9) {
              warned = phaseT;
              fx('Scorching!', 'bad', 'scorch');
            }
          }
          if (sim.heatProgress >= 1) {
            heat = clamp((qn ? qs / qn : 0) - scorch * 0.25 - Math.max(0, phaseT - 9) * 0.015, 0, 1);
            fx('Workable!', 'good', 'workable');
            toShape();
          } else if (phaseT >= HEAT_LIMIT) {
            heat = clamp((qn ? qs / qn : 0) * sim.heatProgress - scorch * 0.25, 0, 1);
            fx('The coals die down', 'bad', 'workable');
            toShape();
          }
          break;
        }
        case 'pause-shape':
        case 'pause-quench':
          if (--pause <= 0) {
            phaseT = 0;
            if (sim.phase === 'pause-shape') sim.phase = 'shape';
            else {
              sim.phase = 'quench';
              sim.temp = Math.max(sim.temp, q2 + 32);
            }
          }
          break;
        case 'shape': {
          phaseT += DT;
          if (pressed && sim.cool <= 0 && sim.strikes.length < a.strikes) {
            sim.cool = 0.3;
            const d = Math.abs(sim.pos - sim.center), cold = sim.temp < lo - 20;
            let q: number, label: string, tone: Fx['tone'];
            if (d <= sim.perfect / 2) [q, label, tone] = [1, 'Perfect!', 'good'];
            else if (d <= sim.good / 2) [q, label, tone] = [0.62, 'Good', 'gold'];
            else [q, label, tone] = [0.15, 'Glancing', 'bad'];
            if (cold) {
              q *= 0.55;
              label += ' (cold)';
            }
            sim.strikes.push(Math.round(q * 100) / 100);
            fx(label, tone, 'strike', q);
            sim.center = 0.22 + rng.next() * 0.56;
            if (sim.strikes.length >= a.strikes) {
              finishShape();
              break;
            }
          }
          sim.cool -= DT;
          sim.pos += dir * speed * DT;
          if (sim.pos > 1) {
            sim.pos = 2 - sim.pos;
            dir = -1;
          }
          if (sim.pos < 0) {
            sim.pos = -sim.pos;
            dir = 1;
          }
          sim.temp = Math.max(15, sim.temp - 3.2 * DT);
          if (phaseT >= SHAPE_LIMIT) {
            fx('The metal has gone cold', 'bad', 'strike', 0);
            finishShape();
          }
          break;
        }
        case 'quench': {
          const t = sim.temp;
          if (pressed) {
            if (t >= q1 && t <= q2) {
              fx('Hiss! A true temper', 'blue', 'quench');
              end(1 - (Math.abs(t - qm) / qh) * 0.3, true);
            } else if (t > q2) {
              fx('Too hot — it warps', 'bad', 'quench');
              end(clamp(0.6 - (t - q2) / 18, 0, 0.6));
            } else {
              fx('Too cool — soft temper', 'bad', 'quench');
              end(clamp(0.6 - (q1 - t) / 18, 0, 0.6));
            }
            return;
          }
          sim.temp -= rate * DT * (0.85 + rng.next() * 0.3);
          if (sim.temp <= q1 - 12) {
            fx('Air-cooled and brittle', 'bad', 'quench');
            end(0.05);
          }
          break;
        }
      }
    },
  };
  return sim;
}
